import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface MemberSchedule {
  id: string;
  member_id: string;
  professor_id: string;
  gym_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  professor?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

export const useMemberSchedule = (gymId?: string | null) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["member-schedule", user?.id, gymId],
    queryFn: async (): Promise<MemberSchedule[]> => {
      const { data, error } = await supabase
        .from("member_schedules")
        .select("*")
        .eq("member_id", user!.id)
        .eq("is_active", true)
        .order("day_of_week");

      if (error) throw error;

      // Fetch professor profiles
      const professorIds = [...new Set((data || []).map(s => s.professor_id))];
      if (professorIds.length === 0) return [];

      const { data: professors } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, avatar_url")
        .in("id", professorIds);

      const profMap = new Map((professors || []).map(p => [p.id, p]));

      return (data || []).map(s => ({
        ...s,
        professor: profMap.get(s.professor_id) || undefined,
      }));
    },
    enabled: !!user && !!gymId,
  });
};
